import { useState, useEffect } from 'react';
import type { MarketData } from '../types';     // adjust path if your barrel is elsewhere

/* ------------------------------------------------------------------ */
/* 1. Optional fallback shown while first fetch is in-flight           */
/* ------------------------------------------------------------------ */

const FALLBACK_DATA: MarketData[] = [];        // leave empty or paste your mock quotes
const REFRESH_MS = 10 * 60 * 1000;             // match Cloud-Function TTL (10 min)

/* ------------------------------------------------------------------ */
/* 2. A helper that grabs JSON from the Cloud Function                 */
/* ------------------------------------------------------------------ */

async function fetchMarketData(): Promise<MarketData[]> {
  const res = await fetch('/api/markets');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/* ------------------------------------------------------------------ */
/* 3. React hook                                                       */
/* ------------------------------------------------------------------ */

export const useMarketData = () => {
  const [marketData, setMarketData] = useState<MarketData[]>(FALLBACK_DATA);
  const [loading, setLoading]     = useState(true);
  const [error,   setError]       = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        if (isMounted) setLoading(true);
        const data = await fetchMarketData();
        if (isMounted) {
          setMarketData(data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) setError('Failed to fetch market data');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();                                   // initial call
    const id = setInterval(load, REFRESH_MS); // refresh every TTL

    return () => {
      isMounted = false;
      clearInterval(id);
    };
  }, []);

  return { marketData, loading, error };
};

/* ------------------------------------------------------------------ */
/* 4. Keep the tiny util function in case something else imports it    */
/* ------------------------------------------------------------------ */

export const getMarketData = (): MarketData[] => FALLBACK_DATA;
const utils = { getMarketData };   // ← give it a name
export default utils;